import React, { useEffect, useState } from 'react';
import { Alert, Box, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PaymentRunService } from '../../../services/paymentRun.service';
import { BlockedBill } from '../../../interfaces/procurement/paymentRun.interface';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import ScrollableTable from '../../common/ScrollableTable';
import { formatMoney } from '../../../utils/format';
import { formatDate } from '../../../utils/dateUtils';

/** Bills held for a price or tax variance: they never enter a payment run until released. */
export const BlockedBills: React.FC = () => {
  const toast = useToast();
  const [rows, setRows] = useState<BlockedBill[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    PaymentRunService.blockedBills(AuthService.getSelectedInstitution()!!)
      .then(setRows)
      .catch(e => toast.showApiError(e, 'Failed to load blocked bills'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box data-help-page="paymentRun.blocked.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Blocked Bills</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        These bills are held for a variance or for an approval. Release a variance hold from the bill's own page (with a justification); a bill waiting for approval is decided in the Approvals inbox.
      </Alert>
      {loading ? <Typography>Loading...</Typography> : rows.length === 0 ? (
        <Typography color="text.secondary">No blocked bills.</Typography>
      ) : (
        <Paper>
          <ScrollableTable>
            <Table data-help-section="paymentRun.blocked.table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="paymentRun.blocked.col.bill">Bill</TableCell><TableCell data-help="paymentRun.blocked.col.supplier">Supplier</TableCell><TableCell data-help="paymentRun.blocked.col.billDate">Bill date</TableCell>
                  <TableCell data-help="paymentRun.blocked.col.due">Due</TableCell><TableCell data-help="paymentRun.blocked.col.amount" align="right">Amount</TableCell>
                  <TableCell data-help="paymentRun.blocked.col.age" align="right">Age (days)</TableCell><TableCell data-help="paymentRun.blocked.col.reason">Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(b => (
                  <TableRow key={b.billId}>
                    <TableCell><Link data-help="paymentRun.blocked.action.open" component={RouterLink} to={`/procurement/supplier-bills/${b.billId}`}>{b.billNumber}</Link></TableCell>
                    <TableCell>{b.supplierName ?? '—'}</TableCell>
                    <TableCell>{formatDate(b.billDate)}</TableCell>
                    <TableCell>{formatDate(b.dueDate)}</TableCell>
                    <TableCell align="right">{formatMoney(b.totalAmount)}</TableCell>
                    <TableCell align="right">{b.ageDays}</TableCell>
                    <TableCell>{b.blockReason ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        </Paper>
      )}
    </Box>
  );
};

export default BlockedBills;
